import { useState, type MouseEvent, type ReactNode } from 'react'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import TaActionButton from '../TaActionButton'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import taapi from '../../services/taapi'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import { ICON_SM } from '../../common/theme'
import type { Question } from '../../types'
import { hasAnswerText } from '../../common/common'
import { runImprove } from '../../services/QuestionActions'

interface SuggestImprovementsButtonProps {
  question: Question
  icon?: ReactNode
  disabled?: boolean
  iconSize?: number
  sx?: any
  testId?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

// Toolbar button that POSTs /api2/rbimprove and patches the returned
// bideval back onto the question via AppContext.updateQuestion. Owns
// its own running state since `improvements` isn't currently a field on
// QuestionProcessingState - the request is short-lived and the per-row
// "in flight" UX is local to this button. Disables when:
//   • the question hasn't been scored yet (improvements need a score
//     baseline to anchor against), or
//   • the score is already 100 (nothing left to improve).
export default function SuggestImprovementsButton({
  question, icon, disabled, iconSize, sx, testId, onMouseEnter, onMouseLeave,
}: SuggestImprovementsButtonProps) {
  const { updateQuestion, getCurrentProject } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  const hasAnswer = hasAnswerText(question)

  const handleClick = async (e?: MouseEvent) => {
    e?.stopPropagation()
    const project = getCurrentProject()
    if (!project) return
    const r = await runImprove(question, project, updateQuestion)
    if (r.success) addToast('success', 'Score estimated')
    else addToast('error', r.msg ?? 'Failed to estimate score')
  }

  const queued = processing.isqueuedimprovements
  const running = processing.isrunningimprovements
  return (
    <TaActionButton
      title={queued ? 'Queued' : 'Suggest Improvements'}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={!hasAnswer || !!disabled}
      running={running}
      queued={queued}
      icon={icon ?? <LightbulbOutlinedIcon sx={{ fontSize: ICON_SM }} />}
      iconSize={iconSize}
      sx={sx}
      testId={testId}
    />
  )
}
